import type { Edge, Node } from '../types';

/** Fields the engine writes for itself; changing them is never a user edit (03-data-contract.md, runtime fields). */
export const RUNTIME_FIELDS: readonly string[] = ['selected', 'dragging', 'resizing', 'positionAbsolute', 'width', 'height'];

export type GraphSnapshot = { nodes: readonly Node[]; edges: readonly Edge[] };

/** One element across an edit: `before: null` = it was added, `after: null` = it was removed. */
export type ElementChange<T> = { id: string; before: T | null; after: T | null };

/** What one user action changed. Only touched elements are listed. */
export type GraphDiff = { nodes: ElementChange<Node>[]; edges: ElementChange<Edge>[] };

type Fields = Record<string, unknown>;

const isPlainObject = (value: unknown): value is Fields => typeof value === 'object' && value !== null && !Array.isArray(value);

const isSame = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item, index) => isSame(item, b[index]));
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return Array.from(keys).every((key) => isSame(a[key], b[key]));
  }
  return false;
};

/** Top-level fields that differ between two versions of an element, runtime fields left out. */
export const getChangedKeys = (before: object, after: object): string[] => {
  const a = before as Fields;
  const b = after as Fields;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).filter((key) => !RUNTIME_FIELDS.includes(key) && !isSame(a[key], b[key]));
};

const diffElements = <T extends { id: string }>(before: readonly T[], after: readonly T[]): ElementChange<T>[] => {
  const afterById = new Map(after.map((element) => [element.id, element]));
  const beforeIds = new Set(before.map((element) => element.id));
  const changes: ElementChange<T>[] = [];
  before.forEach((element) => {
    const next = afterById.get(element.id);
    if (!next) changes.push({ id: element.id, before: element, after: null });
    else if (next !== element && getChangedKeys(element, next).length > 0) changes.push({ id: element.id, before: element, after: next });
  });
  after.forEach((element) => {
    if (!beforeIds.has(element.id)) changes.push({ id: element.id, before: null, after: element });
  });
  return changes;
};

/** The elements one action added, removed or edited (runtime-only changes such as selection do not count). */
export const diffGraph = (before: GraphSnapshot, after: GraphSnapshot): GraphDiff => ({
  nodes: diffElements(before.nodes, after.nodes),
  edges: diffElements(before.edges, after.edges),
});

export const isEmptyGraphDiff = (diff: GraphDiff): boolean => diff.nodes.length === 0 && diff.edges.length === 0;

const swap = <T>({ id, before, after }: ElementChange<T>): ElementChange<T> => ({ id, before: after, after: before });

/** The diff that takes the graph back: additions become removals and edits run the other way. */
export const invertGraphDiff = (diff: GraphDiff): GraphDiff => ({ nodes: diff.nodes.map(swap), edges: diff.edges.map(swap) });

/** `current` with the fields `before` → `after` changed copied over (or dropped), keeping the key order. */
const withChangedFields = <T extends object>(current: T, before: T, after: T): T => {
  const source = current as Fields;
  const target = after as Fields;
  const changed = new Set(getChangedKeys(before, after));
  const kept = Object.entries(source)
    .filter(([key]) => !changed.has(key) || key in target)
    .map(([key, value]) => [key, changed.has(key) ? target[key] : value]);
  const added = Array.from(changed)
    .filter((key) => key in target && !(key in source))
    .map((key) => [key, target[key]]);
  return Object.fromEntries([...kept, ...added]) as T;
};

const applyElementChanges = <T extends { id: string }>(current: readonly T[], changes: readonly ElementChange<T>[]): T[] =>
  changes.reduce<T[]>((elements, { id, before, after }) => {
    const index = elements.findIndex((element) => element.id === id);
    if (!after) return index < 0 ? elements : elements.filter((element) => element.id !== id);
    if (!before) return index < 0 ? [...elements, after] : elements.map((element) => (element.id === id ? after : element));
    // Gone meanwhile (another user deleted it): nothing to edit.
    if (index < 0) return elements;
    return elements.map((element) => (element.id === id ? withChangedFields(element, before, after) : element));
  }, [...current]);

/**
 * Applies `diff` to the current graph. Edited elements only get the fields the diff changed, so other users' edits to
 * other elements or other fields stay; elements removed meanwhile are skipped.
 */
export const applyGraphDiff = <TNode extends Node, TEdge extends Edge>(
  current: { nodes: readonly TNode[]; edges: readonly TEdge[] },
  diff: GraphDiff,
): { nodes: TNode[]; edges: TEdge[] } => ({
  nodes: applyElementChanges(current.nodes, diff.nodes as ElementChange<TNode>[]),
  edges: applyElementChanges(current.edges, diff.edges as ElementChange<TEdge>[]),
});
